import { createFileRoute, useParams, useNavigate } from '@tanstack/react-router';
import { useEffect, useState, type ReactElement } from 'react';
import { useViewport } from '@/lib/hooks/use-viewport';
import { DAdminShell } from '@/components/organisms/admin-shell';
import { AdminDesktopOnlyNotice } from '@/components/organisms/admin-desktop-only-notice';
import { ModerationQueuePane } from '@/components/organisms/moderation-queue-pane';
import { CandidateReviewCentre } from '@/components/organisms/candidate-review-centre';
import { CandidateDecisionPanel } from '@/components/organisms/candidate-decision-panel';
import { ApproveCandidateModal } from '@/components/organisms/approve-candidate-modal';
import { RejectCandidateModal } from '@/components/organisms/reject-candidate-modal';
import { MergeCandidateModal } from '@/components/organisms/merge-candidate-modal';
import {
  useApproveModerationCandidate,
  useRejectModerationCandidate,
  useStartEditHandoff,
  useMergeModerationCandidate,
  useModerationCandidate,
  useModerationCandidates,
} from '@/lib/queries/moderation';
import { useSearchPieces } from '@/lib/queries/pieces';
import { useDebouncedValue } from '@/lib/hooks/use-debounced-value';
import type { MergeTargetOption } from '@/components/organisms/merge-candidate-modal';
import { emitToast } from '@/lib/toast-bus';
import { ApiError } from '@/lib/api-client';
import { Link } from '@tanstack/react-router';

const PAGE_SIZE = 20;
const NOT_FOUND_STATUS = 404;
const CONFLICT_STATUS = 409;
const SEARCH_DEBOUNCE_MS = 250;

const APPROVE_SUCCESS_MESSAGE = 'Approved · piece published';
const APPROVE_STALE_MESSAGE =
  'This candidate changed since you loaded it. Refresh and try again.';
const APPROVE_ERROR_MESSAGE = 'Could not approve. Try again.';
const REJECT_SUCCESS_MESSAGE = 'Rejected';
const REJECT_STALE_MESSAGE =
  'This candidate changed since you loaded it. Refresh and try again.';
const REJECT_ERROR_MESSAGE = 'Could not reject. Try again.';
const EDIT_HANDOFF_ERROR_MESSAGE = 'Could not open the editor. Try again.';
const MERGE_SUCCESS_MESSAGE = 'Merged';
const MERGE_STALE_MESSAGE =
  'This candidate changed since you loaded it. Refresh and try again.';
const MERGE_TARGET_MISSING_MESSAGE = 'That target piece no longer exists. Refresh and try again.';
const MERGE_ERROR_MESSAGE = 'Could not merge. Try again.';
const QUEUE_PATH = '/admin/queue';

function isStatus(error: unknown, status: number): boolean {
  return error instanceof ApiError && error.status === status;
}

function is404(error: unknown): boolean {
  return isStatus(error, NOT_FOUND_STATUS);
}

function mergeErrorMessage(error: unknown): string {
  if (isStatus(error, CONFLICT_STATUS)) return MERGE_STALE_MESSAGE;
  if (isStatus(error, NOT_FOUND_STATUS)) return MERGE_TARGET_MISSING_MESSAGE;
  return MERGE_ERROR_MESSAGE;
}

function LoadingShell(): ReactElement {
  return (
    <DAdminShell section="queue" title="Moderation queue">
      <div className="p-8 text-[13px] text-[var(--ink-3)]">Loading…</div>
    </DAdminShell>
  );
}

function CandidateNotFoundScreen(): ReactElement {
  return (
    <DAdminShell section="queue" title="Moderation queue">
      <div className="p-8 flex flex-col gap-2 items-start">
        <h2 className="text-[18px] font-semibold text-[var(--ink)]">Candidate not found</h2>
        <p className="text-[13px] text-[var(--ink-3)]">
          This submission may have been reviewed or removed.
        </p>
        <Link
          to="/admin/queue"
          className="text-[13px] text-[var(--accent)] hover:underline"
        >
          ← Back to queue
        </Link>
      </div>
    </DAdminShell>
  );
}

function AdminQueueDetailPage(): ReactElement {
  const viewport = useViewport();
  const navigate = useNavigate();
  const { pieceId } = useParams({ from: '/admin/queue/$pieceId' });
  const [reason, setReason] = useState('');
  const [selectedMatchId, setSelectedMatchId] = useState<string | null>(null);
  const [queuePage, setQueuePage] = useState(0);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);
  const [transferArrangementIds, setTransferArrangementIds] = useState<string[]>([]);
  const [transferSourceIds, setTransferSourceIds] = useState<string[]>([]);
  const [mergeSearch, setMergeSearch] = useState('');

  const detail = useModerationCandidate(pieceId);
  const queue = useModerationCandidates({ page: queuePage, size: PAGE_SIZE });
  const approve = useApproveModerationCandidate();
  const reject = useRejectModerationCandidate();
  const editHandoff = useStartEditHandoff();
  const merge = useMergeModerationCandidate();
  const debouncedMergeSearch = useDebouncedValue(mergeSearch.trim(), SEARCH_DEBOUNCE_MS);
  const searchPieces = useSearchPieces(debouncedMergeSearch);

  useEffect(() => {
    setSelectedMatchId(null);
    setReason('');
    setIsApproveModalOpen(false);
    setIsRejectModalOpen(false);
    setIsMergeModalOpen(false);
    setTransferArrangementIds([]);
    setTransferSourceIds([]);
    setMergeSearch('');
  }, [pieceId]);

  if (viewport !== 'desktop') return <AdminDesktopOnlyNotice />;
  if (detail.isError && is404(detail.error)) return <CandidateNotFoundScreen />;
  if (detail.isLoading || !detail.data) return <LoadingShell />;

  const { piece, closestMatches, arrangements, sources } = detail.data;
  const closestMatchIds = new Set(closestMatches.map((match) => match.pieceId));
  const searchResults: MergeTargetOption[] = (searchPieces.data?.content ?? [])
    .filter((result) => !closestMatchIds.has(result.pieceId))
    .map((result) => ({
      pieceId: result.pieceId,
      prettyName: result.prettyName,
      composerName: result.composerName,
      similarity: 0,
    }));
  const mergeTargets: MergeTargetOption[] = [...closestMatches, ...searchResults];
  const effectiveSelected: MergeTargetOption | null =
    mergeTargets.find((target) => target.pieceId === selectedMatchId)
    ?? closestMatches[0]
    ?? null;

  const openMergeModal = (): void => {
    setTransferArrangementIds(arrangements.map((a) => a.arrangementId));
    setTransferSourceIds(sources.map((s) => s.pieceSourceId));
    setIsMergeModalOpen(true);
  };

  const toggleTransferArrangement = (arrangementId: string): void => {
    setTransferArrangementIds((current) =>
      current.includes(arrangementId)
        ? current.filter((id) => id !== arrangementId)
        : [...current, arrangementId],
    );
  };

  const toggleTransferSource = (pieceSourceId: string): void => {
    setTransferSourceIds((current) =>
      current.includes(pieceSourceId)
        ? current.filter((id) => id !== pieceSourceId)
        : [...current, pieceSourceId],
    );
  };

  const handleApproveConfirm = (): void => {
    approve.mutate(
      { pieceId, expectedVersion: piece.version },
      {
        onSuccess: () => {
          emitToast({ message: APPROVE_SUCCESS_MESSAGE });
          setIsApproveModalOpen(false);
          void navigate({ to: QUEUE_PATH });
        },
        onError: (error: unknown) => {
          emitToast({
            message: isStatus(error, CONFLICT_STATUS)
              ? APPROVE_STALE_MESSAGE
              : APPROVE_ERROR_MESSAGE,
          });
          setIsApproveModalOpen(false);
          void detail.refetch();
        },
      },
    );
  };

  const handleRejectConfirm = (): void => {
    reject.mutate(
      { pieceId, expectedVersion: piece.version, reason: reason.trim() },
      {
        onSuccess: () => {
          emitToast({ message: REJECT_SUCCESS_MESSAGE });
          setIsRejectModalOpen(false);
          void navigate({ to: QUEUE_PATH });
        },
        onError: (error: unknown) => {
          emitToast({
            message: isStatus(error, CONFLICT_STATUS)
              ? REJECT_STALE_MESSAGE
              : REJECT_ERROR_MESSAGE,
          });
          setIsRejectModalOpen(false);
          void detail.refetch();
        },
      },
    );
  };

  const handleEditHandoff = (): void => {
    editHandoff.mutate(
      { pieceId },
      {
        onSuccess: ({ redirectUrl }) => {
          void navigate({ to: redirectUrl });
        },
        onError: () => {
          emitToast({ message: EDIT_HANDOFF_ERROR_MESSAGE });
        },
      },
    );
  };

  const handleMergeConfirm = (): void => {
    if (!effectiveSelected) return;
    merge.mutate(
      {
        pieceId,
        expectedVersion: piece.version,
        targetPieceId: effectiveSelected.pieceId,
        transferredArrangementIds: transferArrangementIds,
        transferredSourceIds: transferSourceIds,
      },
      {
        onSuccess: () => {
          emitToast({ message: MERGE_SUCCESS_MESSAGE });
          setIsMergeModalOpen(false);
          void navigate({ to: QUEUE_PATH });
        },
        onError: (error: unknown) => {
          emitToast({ message: mergeErrorMessage(error) });
          setIsMergeModalOpen(false);
          void detail.refetch();
        },
      },
    );
  };

  return (
    <DAdminShell section="queue" title="Moderation queue">
      <div className="grid grid-cols-[280px_1fr_320px] h-full">
        <ModerationQueuePane
          page={queue.data}
          isLoading={queue.isLoading}
          currentPage={queuePage}
          onPageChange={setQueuePage}
        />
        <CandidateReviewCentre
          candidate={piece}
          closestMatches={closestMatches}
          selectedMatch={effectiveSelected}
          onPickMatch={setSelectedMatchId}
        />
        <CandidateDecisionPanel
          reason={reason}
          onReasonChange={setReason}
          onApprove={() => setIsApproveModalOpen(true)}
          onMerge={openMergeModal}
          onReject={() => setIsRejectModalOpen(true)}
          onEditHandoff={handleEditHandoff}
        />
      </div>
      {isApproveModalOpen && (
        <ApproveCandidateModal
          candidateTitle={piece.prettyName}
          isApproving={approve.isPending}
          onConfirm={handleApproveConfirm}
          onCancel={() => setIsApproveModalOpen(false)}
        />
      )}
      {isRejectModalOpen && (
        <RejectCandidateModal
          candidateTitle={piece.prettyName}
          reason={reason}
          isRejecting={reject.isPending}
          onConfirm={handleRejectConfirm}
          onCancel={() => setIsRejectModalOpen(false)}
        />
      )}
      {isMergeModalOpen && (
        <MergeCandidateModal
          candidateTitle={piece.prettyName}
          targets={closestMatches}
          selectedTargetId={effectiveSelected?.pieceId ?? null}
          onSelectTarget={setSelectedMatchId}
          arrangements={arrangements}
          sources={sources}
          selectedArrangementIds={transferArrangementIds}
          selectedSourceIds={transferSourceIds}
          onToggleArrangement={toggleTransferArrangement}
          onToggleSource={toggleTransferSource}
          searchValue={mergeSearch}
          onSearchChange={setMergeSearch}
          searchResults={searchResults}
          isMerging={merge.isPending}
          onConfirm={handleMergeConfirm}
          onCancel={() => setIsMergeModalOpen(false)}
        />
      )}
    </DAdminShell>
  );
}

export const Route = createFileRoute('/admin/queue/$pieceId')({
  component: AdminQueueDetailPage,
});
